import { useState, useEffect, useRef } from 'react';
import { Navigate, Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSession, authClient } from '../lib/auth-client';
import { Badge, Button } from '@img/ui';
import { cn } from '@img/ui';
import { BrandLogo } from '../components/BrandLogo';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { RamhCredit } from '../components/RamhCredit';
import { NotificationsBell } from '../components/NotificationsBell';
import { useUIStore } from '../store/ui';
import { api, type ApiSuccess } from '../lib/api';

interface AuthLayoutProps {
  /** الأدوار المسموح لها (افتراضي: أي مستخدم مسجّل) */
  allowedRoles?: string[];
}

/**
 * 🔒 Layout للمناطق المصادقة
 * - يحمي الصفحة (يعيد للـ /login لو غير مسجّل)
 * - يفحص الدور (لو محدّد)
 * - يعرض sidebar للوحة التحكم
 */
export function AuthLayout({ allowedRoles }: AuthLayoutProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language !== 'en';  // العربي (وأي لغة غير الإنجليزي) = RTL
  const { data: session, isPending } = useSession();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // 🎯 الأقسام المفتوحة (accordion state)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('admin_sidebar_sections');
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return {};
  });

  const toggleSection = (key: string) => {
    setOpenSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('admin_sidebar_sections', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // ⚠️ مهم: كل الـ Hooks (useState, useQuery, …) يجب أن تستدعى قبل أي return شرطي.
  const role = (session?.user as { role?: string } | undefined)?.role;
  const isAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';

  // 🔔 جلب عدّاد العناصر التي تحتاج اهتماماً (تحديث كل 60 ثانية)
  const { data: pendingCountsData } = useQuery({
    queryKey: ['admin', 'pending-counts'],
    queryFn: () => api.get<ApiSuccess<Record<string, number>>>('/api/admin/stats/pending-counts'),
    enabled: isAdmin && !!session?.user,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const pendingCounts: Record<string, number> = pendingCountsData?.data ?? {};

  // ━━━ بعد استدعاء كل الـ hooks، نتعامل مع returns الشرطية ━━━
  if (isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin text-3xl text-navy">⟳</div>
      </div>
    );
  }

  if (!session?.user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(role || '')) {
    return <Navigate to="/" replace />;
  }

  // 👤 قائمة المستخدم — مجمّعة لأقسام مثل قائمة الإدارة
  const userSections: Array<{
    heading?: string;
    headingIcon?: string;
    standalone?: boolean;
    items: Array<{ to: string; label: string; icon: string; end?: boolean }>;
  }> = [
    {
      standalone: true,
      items: [
        { to: '/dashboard',         label: t('dashboard.main'),    icon: '🏠', end: true },
        { to: '/dashboard/profile', label: t('dashboard.profile'), icon: '👤' },
      ],
    },
    {
      heading: t('dashboard.section_properties_auctions'),
      headingIcon: '🏘️',
      items: [
        { to: '/dashboard/properties', label: t('dashboard.my_properties'), icon: '🏠' },
        { to: '/dashboard/auctions',   label: t('dashboard.my_auctions'),   icon: '🔨' },
        { to: '/dashboard/favorites',  label: t('dashboard.favorites'),     icon: '❤️' },
        { to: '/dashboard/alerts',     label: t('dashboard.alerts'),         icon: '🔔' },
      ],
    },
    {
      heading: t('dashboard.section_requests_comm'),
      headingIcon: '📋',
      items: [
        { to: '/dashboard/requests',        label: t('dashboard.my_requests'),     icon: '📋' },
        { to: '/dashboard/notifications',   label: t('dashboard.notifications'),    icon: '🔔' },
        { to: '/dashboard/my-associations', label: t('dashboard.my_associations'), icon: '🏛️' },
      ],
    },
    {
      heading: t('dashboard.section_wallet_subs'),
      headingIcon: '💳',
      items: [
        { to: '/dashboard/wallet', label: t('dashboard.wallet'), icon: '💳' },
        ...(role === 'BROKER' ? [{ to: '/dashboard/subscription', label: t('dashboard.subscription'), icon: '💎' }] : []),
      ],
    },
  ];

  // 🗂️ القائمة الإدارية مقسّمة لأقسام
  // التصميم موحّد: لون واحد للأقسام الرئيسية (gold)، لون واحد للفرعية (navy/أبيض)
  const adminSections: Array<{
    heading?: string;
    headingIcon?: string;
    standalone?: boolean; // true = ما له heading (يظهر مباشرة)
    items: Array<{ to: string; label: string; icon: string; end?: boolean }>;
  }> = [
    {
      heading: t('admin.section_users'),
      headingIcon: '👑',
      items: [
        { to: '/admin',                 label: t('admin.dashboard'),         icon: '🎯', end: true },
        { to: '/admin/users',           label: t('admin.users'),             icon: '👤' },
        { to: '/admin/brokers',         label: t('admin.brokers'),           icon: '🤝' },
        { to: '/admin/fal-licenses',    label: 'رخص فال',                    icon: '🪪' },
        { to: '/admin/profile-changes', label: t('admin.profile_changes'),   icon: '📝' },
        { to: '/admin/invitations',     label: t('admin.invitations'),       icon: '📨' },
      ],
    },
    {
      heading: t('admin.section_re_auctions'),
      headingIcon: '🏘️',
      items: [
        { to: '/admin/properties',       label: t('admin.properties'),       icon: '🏠' },
        { to: '/admin/auctions',         label: t('admin.auctions'),         icon: '🔨' },
        { to: '/admin/auction-requests', label: t('admin.auction_requests'), icon: '📨' },
      ],
    },
    {
      heading: t('admin.section_mullak'),
      headingIcon: '🏛️',
      items: [
        { to: '/admin/companies',     label: t('admin.companies'),    icon: '🏢' },
        { to: '/admin/associations',  label: t('admin.associations'), icon: '🏛️' },
        { to: '/admin/facility',      label: t('admin.facility'),     icon: '🔧' },
      ],
    },
    {
      heading: t('admin.section_finance'),
      headingIcon: '💰',
      items: [
        { to: '/admin/discount-codes', label: t('admin.discount_codes'), icon: '💸' },
        { to: '/admin/payments',       label: t('admin.payments'),       icon: '💳' },
      ],
    },
    {
      heading: t('admin.section_requests_comm'),
      headingIcon: '📨',
      items: [
        { to: '/admin/requests',    label: t('admin.requests'),    icon: '📋' },
        { to: '/admin/complaints',  label: 'الشكاوى',              icon: '📣' },
        { to: '/admin/messages',    label: t('admin.messages'),    icon: '✉️' },
        { to: '/admin/content',     label: t('admin.content'),     icon: '📦' },
      ],
    },
    {
      heading: t('admin.section_settings'),
      headingIcon: '⚙️',
      items: [
        { to: '/admin/activity-log', label: t('admin.activity_log'), icon: '📊' },
        { to: '/admin/messages-log', label: t('admin.messages_log'), icon: '📬' },
        { to: '/admin/credentials',  label: 'الوثائق والشهادات',     icon: '🏛️' },
        { to: '/admin/settings',     label: t('admin.settings'),     icon: '⚙️' },
      ],
    },
    ...(role === 'SUPER_ADMIN' ? [{
      heading: t('admin.section_super'),
      headingIcon: '🔒',
      items: [
        { to: '/admin/integrations', label: t('admin.integrations'), icon: '🔌' },
      ],
    }] : []),
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-light)]">
      {/* 📱 Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setMobileOpen(false)} />
      )}

      {/* 📑 Sidebar — مثبّت دائماً، يتحرّك بحسب اتجاه اللغة (يمين بالعربي، يسار بالإنجليزي) */}
      <aside
        className={cn(
          'bg-navy-darker text-white overflow-y-auto z-50 transition-transform',
          'fixed top-0 h-screen w-72 md:w-64',
          isRtl ? 'right-0' : 'left-0',
          mobileOpen
            ? 'translate-x-0'
            : isRtl
              ? 'translate-x-full md:translate-x-0'   // RTL: ينزلق لليمين عند الإقفال
              : '-translate-x-full md:translate-x-0', // LTR: ينزلق لليسار عند الإقفال
        )}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <Link to="/" onClick={() => setMobileOpen(false)}>
            <BrandLogo size="md" inverted />
          </Link>
          <button onClick={() => setMobileOpen(false)} className="md:hidden text-white/70 hover:text-white text-2xl" aria-label="إغلاق">✕</button>
        </div>

        <nav className="py-4 space-y-1">
          {/* 👤 قسم المستخدم */}
          <div className="px-5 text-[10px] uppercase font-black tracking-widest text-gold/70 mb-2">{t('dashboard.user_section')}</div>
          {userSections.map((section, i) => (
            <SidebarSection
              key={`user-${i}`}
              section={section}
              sectionKey={`user_${section.heading ?? `__standalone_${i}`}`}
              isOpen={section.standalone || (openSections[`user_${section.heading ?? `__standalone_${i}`}`] ?? section.items.some((it) => it.end ? location.pathname === it.to : location.pathname.startsWith(it.to)))}
              onToggle={toggleSection}
              onItemClick={() => setMobileOpen(false)}
            />
          ))}

          {/* 👑 قسم الإدارة */}
          {isAdmin && (
            <>
              <div className="px-5 text-[10px] uppercase font-black tracking-widest text-gold/70 mt-6 mb-2">{t('dashboard.admin_section')}</div>
              {adminSections.map((section, i) => (
                <SidebarSection
                  key={`admin-${i}`}
                  section={section}
                  sectionKey={`admin_${section.heading ?? `__standalone_${i}`}`}
                  isOpen={section.standalone || (openSections[`admin_${section.heading ?? `__standalone_${i}`}`] ?? section.items.some((it) => it.end ? location.pathname === it.to : location.pathname.startsWith(it.to)))}
                  onToggle={toggleSection}
                  onItemClick={() => setMobileOpen(false)}
                  pendingCounts={pendingCounts}
                />
              ))}
            </>
          )}
        </nav>

        {/* تذييل الـ sidebar — RAMH credit فقط (تسجيل الخروج انتقل للهيدر العلوي) */}
        <div className="p-5 mt-auto border-t border-white/10">
          <div className="text-center"><RamhCredit inverted /></div>
        </div>
      </aside>

      {/* 📄 Main + Top Header — يحجز مساحة لـ sidebar (يمين بالعربي / يسار بالإنجليزي) */}
      <div className={cn(
        'min-h-screen flex flex-col',
        isRtl ? 'md:mr-64' : 'md:ml-64',
      )}>
        <DashboardHeader
          user={session.user}
          role={role}
          isAdmin={isAdmin}
          onOpenMobileNav={() => setMobileOpen(true)}
        />

        <main className="flex-1 p-6 pb-[calc(80px+env(safe-area-inset-bottom))] md:pb-6 flex flex-col">
          <div className="flex-1"><Outlet /></div>
          <footer className="text-center pt-6 mt-6 border-t border-gray-200">
            <RamhCredit />
          </footer>
        </main>
      </div>

      {/* 📱 Bottom Nav للموبايل — كأنه تطبيق */}
      <MobileBottomNav />
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ هيدر لوحة التحكم العلوي ━━━━━━━━━━━━━━━━━━━━
//
// 🎨 مستوحى من PublicLayout — أبيض، sticky، بظل خفيف
// يحتوي:
//   - يمين: ترحيب باسم المستخدم + شارات الدور والتوثيق
//   - يسار: NotificationsBell + زر اللغة + قائمة المستخدم (تحتوي تسجيل الخروج)
//
function DashboardHeader({ user, role, isAdmin, onOpenMobileNav }: {
  user: { id: string; name: string; email?: string | null };
  role?: string;
  isAdmin: boolean;
  onOpenMobileNav: () => void;
}) {
  const { t, i18n } = useTranslation();
  const setLanguage = useUIStore((s) => s.setLanguage);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // إقفال القائمة لما المستخدم يضغط خارجها
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const toggleLang = () => {
    const next = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(next);
    setLanguage(next);
  };

  const verificationTier = (user as { verificationTier?: string }).verificationTier;
  const tier = verificationTier && verificationTier !== 'NONE' ? verificationTier : null;

  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur shadow-sm border-b border-gray-100">
      <div className="px-4 md:px-6 h-16 flex items-center justify-between gap-3">
        {/* 📱 زر القائمة على الموبايل */}
        <button
          onClick={onOpenMobileNav}
          className="md:hidden w-10 h-10 rounded-xl bg-navy-dark text-white flex items-center justify-center text-xl shrink-0"
          aria-label="فتح القائمة"
        >
          ☰
        </button>

        {/* 🌟 يمين: ترحيب */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="hidden sm:flex w-10 h-10 rounded-xl bg-gradient-to-br from-navy to-navy-dark text-white items-center justify-center text-lg font-black shrink-0">
            {user.name.charAt(0)}
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-ink-muted leading-none">{t('common.welcome', 'مرحباً،')}</div>
            <div className="font-black text-navy-dark text-sm truncate flex items-center gap-2">
              <span className="truncate">{user.name}</span>
              {role && (
                <Badge color={isAdmin ? 'gold' : 'navy'}>{role}</Badge>
              )}
              {tier && <Badge color="green">{tier}</Badge>}
            </div>
          </div>
        </div>

        {/* 🌐 يسار: الأكشنز */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* رابط للموقع العام */}
          <Link to="/" className="hidden md:block">
            <Button variant="ghost" size="sm" title={t('nav.site')}>{t('nav.site')}</Button>
          </Link>

          {/* مبدّل اللغة */}
          <Button variant="ghost" size="sm" onClick={toggleLang} title={i18n.language === 'ar' ? 'English' : 'العربية'}>
            {i18n.language === 'ar' ? 'EN' : 'ع'}
          </Button>

          {/* جرس الإشعارات */}
          <NotificationsBell />

          {/* قائمة المستخدم (avatar + dropdown) */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-xl hover:bg-gray-100 transition"
              title="حسابي"
            >
              <div className="w-8 h-8 rounded-lg bg-navy-dark text-white flex items-center justify-center text-sm font-black">
                {user.name.charAt(0)}
              </div>
              <span className="hidden md:block text-xs text-ink-muted">▼</span>
            </button>

            {menuOpen && (
              <div className={cn(
                'absolute top-full mt-2 w-64 bg-white rounded-2xl shadow-brand-xl border border-gray-100 overflow-hidden z-50',
                i18n.language === 'en' ? 'right-0' : 'left-0',
              )}>
                {/* رأس القائمة */}
                <div className="bg-gradient-to-l from-navy-dark to-navy text-white px-4 py-3">
                  <div className="font-black truncate">{user.name}</div>
                  {user.email && (
                    <div className="text-xs text-white/70 truncate" dir="ltr">{user.email}</div>
                  )}
                  <div className="flex gap-1 mt-1.5">
                    {role && <Badge color={isAdmin ? 'gold' : 'navy'}>{role}</Badge>}
                    {tier && <Badge color="green">{tier}</Badge>}
                  </div>
                </div>
                {/* عناصر القائمة */}
                <div className="py-1">
                  <Link
                    to="/dashboard/profile"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-ink hover:bg-gray-50 transition"
                  >
                    <span>👤</span>
                    <span>{t('dashboard.profile')}</span>
                  </Link>
                  <Link
                    to="/dashboard/notifications"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-ink hover:bg-gray-50 transition"
                  >
                    <span>🔔</span>
                    <span>{t('dashboard.notifications')}</span>
                  </Link>
                  <Link
                    to="/dashboard/wallet"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-ink hover:bg-gray-50 transition"
                  >
                    <span>💳</span>
                    <span>{t('dashboard.wallet')}</span>
                  </Link>
                </div>
                {/* تسجيل الخروج (بارز، أحمر) */}
                <div className="border-t border-gray-100 p-2">
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      authClient.signOut().then(() => window.location.href = '/');
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50 rounded-xl transition"
                  >
                    <span>↪</span>
                    <span>{t('nav.logout')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ مكوّن قسم القائمة الجانبية ━━━━━━━━━━━━━━━━━━━━
//
// 🎨 التصميم الموحّد (لون واحد للرئيسي + لون واحد للفرعي):
//   • القسم الرئيسي (heading): زرّ بحدود ذهبية + خلفية شفافة + سهم قابل للطي
//   • العنصر المستقل (standalone): مثل القسم الرئيسي لكن بدون collapse
//   • العناصر الفرعية: نص أبيض/رمادي مع شريط ذهبي على اليمين عند التفعيل
//
type SidebarSectionData = {
  heading?: string;
  headingIcon?: string;
  standalone?: boolean;
  items: Array<{ to: string; label: string; icon: string; end?: boolean }>;
};

function SidebarSection({ section, sectionKey, isOpen, onToggle, onItemClick, pendingCounts }: {
  section: SidebarSectionData;
  sectionKey: string;
  isOpen: boolean;
  onToggle: (key: string) => void;
  onItemClick: () => void;
  pendingCounts?: Record<string, number>;
}) {
  // 🔔 إجمالي عدّاد القسم = مجموع كل العدّادات لعناصره
  const sectionPendingTotal = section.items.reduce(
    (sum, item) => sum + (pendingCounts?.[item.to] ?? 0),
    0,
  );

  // 🌟 عنصر مستقل (لوحة الإدارة، الرئيسية…) — تصميم خفيف
  if (section.standalone) {
    return (
      <div className="px-3 space-y-0.5">
        {section.items.map((item) => {
          const count = pendingCounts?.[item.to] ?? 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onItemClick}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-bold transition',
                  isActive
                    ? 'bg-gold/20 text-white'
                    : 'text-gray-200 hover:bg-white/5 hover:text-white',
                )
              }
            >
              <span className="text-base shrink-0 w-5 text-center">{item.icon}</span>
              <span className="flex-1 truncate text-start">{item.label}</span>
              {count > 0 && <CountBadge count={count} small />}
            </NavLink>
          );
        })}
      </div>
    );
  }

  // 🎯 قسم رئيسي قابل للطي (Accordion) — تصميم خفيف كهيكل
  return (
    <div className="px-3">
      <button
        onClick={() => onToggle(sectionKey)}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-bold transition',
          isOpen
            ? 'text-gold'
            : 'text-gray-300 hover:text-white',
        )}
      >
        <span className="text-base shrink-0 w-5 text-center">{section.headingIcon}</span>
        <span className="flex-1 text-start truncate">{section.heading}</span>
        {/* 🔔 شارة الإشعار — تظهر فقط لو فيه شي غير مقروء (مهما كانت الحالة) */}
        {sectionPendingTotal > 0 && (
          <CountBadge count={sectionPendingTotal} small pulse={!isOpen} />
        )}
        <span
          className="text-[9px] opacity-50 shrink-0 transition-transform"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transitionDuration: '350ms',
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          ▼
        </span>
      </button>

      {/* 📦 العناصر الفرعية — تنزل بشكل خفيف هيكلي مع stagger */}
      <div
        className="overflow-hidden"
        style={{
          maxHeight: isOpen ? `${section.items.length * 36 + 8}px` : '0px',
          opacity: isOpen ? 1 : 0,
          transition: 'max-height 350ms cubic-bezier(0.4, 0, 0.2, 1), opacity 250ms ease',
        }}
      >
        <div className="ms-5 ps-3 border-s border-white/10 space-y-0 mt-0.5 mb-1">
          {section.items.map((item, idx) => {
            const count = pendingCounts?.[item.to] ?? 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onItemClick}
                style={{
                  // 🪶 stagger خفيف — كل عنصر يظهر بعد اللي قبله بشوي
                  transitionDelay: isOpen ? `${idx * 35}ms` : '0ms',
                  transform: isOpen ? 'translateY(0)' : 'translateY(-6px)',
                  opacity: isOpen ? 1 : 0,
                  transitionProperty: 'transform, opacity',
                  transitionDuration: '300ms',
                  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 px-2 py-1.5 rounded-md text-xs',
                    isActive
                      ? 'text-gold font-bold'
                      : 'text-gray-400 hover:text-white',
                  )
                }
              >
                <span className="flex-1 truncate text-start">{item.label}</span>
                {count > 0 && <CountBadge count={count} small />}
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// 🔔 شارة عدّاد بسيطة (أحمر + رقم)
function CountBadge({ count, small, pulse }: { count: number; small?: boolean; pulse?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-full bg-red-500 text-white font-bold leading-none',
        small ? 'min-w-[18px] h-[18px] px-1 text-[10px]' : 'min-w-[22px] h-[22px] px-1.5 text-[11px]',
        pulse && 'animate-pulse ring-2 ring-red-300/50',
      )}
      title={`${count} عنصر يحتاج اهتماماً`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
